/**
 * Maandrapport's view of a snapshot: its sections, the view of each date range and the details behind a figure,
 * each fetched once through the query cache by snapshot id (a snapshot never changes), so every figure of one page
 * view comes from the same snapshot. The section loading itself is Bezettingsgraad's (`cachedSectionLoader`).
 */

import type { QueryClient } from "@tanstack/react-query";

import {
  type ApiError,
  fetchMaandrapportDetail,
  fetchMaandrapportSection,
  fetchMaandrapportView,
  type GetMaandrapportDetailResponse,
  maandrapportDetailKey,
  maandrapportSectionKey,
  type MaandrapportView as ApiView,
  maandrapportViewKey,
} from "@/lib/api";
import { apiErrorCode } from "@/lib/api-errors";
import { logger } from "@/lib/logger";

import { cachedSectionLoader } from "../bezetting/snapshot-sections";
import type {
  ActivityDetail,
  AgeingDetail,
  ClientDetail,
  ClientsDetail,
  ConcentrationDetail,
  DetailKind,
  DetailLoader,
  DetailRequest,
  DueInvoicesDetail,
  DueMonthDetail,
  InvoiceDetail,
  MaandrapportView,
  NewClientsDetail,
  RegionDetail,
  RentalsDetail,
  ReturningDetail,
  SectionLoader,
  SnapshotRef,
  TransportsDetail,
  UnbilledDetail,
  ViewLoader,
} from "./contract";

/**
 * A renderer shape with its named keys ("kraanverhuur", "BK") as plain strings: what the API's schema says. The API
 * gives the keys as strings; the renderer reads them through its own lists of activities, regions and classes.
 */
type Wide<T> = T extends string
  ? string
  : T extends readonly (infer U)[]
    ? readonly Wide<U>[]
    : T extends object
      ? { readonly [K in keyof T]: Wide<T[K]> }
      : T;

/**
 * The API's view as the renderer reads it. The assignment is the check: a field renamed, dropped or turned nullable
 * on one side and not the other is a type error here.
 */
function asView(view: ApiView): MaandrapportView {
  const checked: Wide<MaandrapportView> = view;
  return checked as MaandrapportView;
}

/** The same check for every detail kind. */
type ApiDetail<K extends DetailKind> = Extract<GetMaandrapportDetailResponse, { kind: K }>;
type Checked<A, R> = A extends Wide<R> ? true : false;
type DetailChecks = [
  Checked<ApiDetail<"activity">, ActivityDetail>,
  Checked<ApiDetail<"region">, RegionDetail>,
  Checked<ApiDetail<"ageing">, AgeingDetail>,
  Checked<ApiDetail<"due_month">, DueMonthDetail>,
  Checked<ApiDetail<"concentration">, ConcentrationDetail>,
  Checked<ApiDetail<"rentals">, RentalsDetail>,
  Checked<ApiDetail<"transports">, TransportsDetail>,
  Checked<ApiDetail<"unbilled">, UnbilledDetail>,
  Checked<ApiDetail<"returning">, ReturningDetail>,
  Checked<ApiDetail<"new_clients">, NewClientsDetail>,
  Checked<ApiDetail<"invoice">, InvoiceDetail>,
  Checked<ApiDetail<"client">, ClientDetail>,
  Checked<ApiDetail<"clients">, ClientsDetail>,
  Checked<ApiDetail<"due_invoices">, DueInvoicesDetail>,
];
/** Every detail the API answers reads as the renderer's shape of its kind. */
export const DETAILS_MATCH: DetailChecks = [
  true,
  true,
  true,
  true,
  true,
  true,
  true,
  true,
  true,
  true,
  true,
  true,
  true,
  true,
];

/** A failure worth asking again: no HTTP answer, or a server error; never a 4xx. */
function retryable(failureCount: number, error: unknown): boolean {
  const status = (error as Partial<ApiError> | null)?.status ?? 0;
  return failureCount < 2 && (status === 0 || status >= 500);
}

interface LoaderOptions {
  /** The snapshot is gone (pruned, or replaced): the host refetches the summary and moves onto the newest. */
  onSnapshotGone: () => void;
  /** The server refuses the app to this person now (403): the host shows the denied page. */
  onDenied: () => void;
  /**
   * The snapshot is of an older schema (409 `snapshot_outdated`), which the ranges cannot be computed from: the host
   * shows Herbereken until a recompute brings a new one.
   */
  onOutdated?: () => void;
}

interface SectionLoaderOptions extends LoaderOptions {
  /** For tests: the request for one section. */
  fetchSection?: (snapshotId: string, key: string) => Promise<Record<string, unknown>>;
}

/** What a failed load means for the page around it: a snapshot gone or outdated, or the app taken away. */
function failureHandler(
  snapshot: SnapshotRef,
  { onSnapshotGone, onDenied, onOutdated }: LoaderOptions,
) {
  return (error: unknown): void => {
    const code = apiErrorCode(error);
    if (code === "snapshot_not_found") {
      logger.debug("[maandrapport]", "snapshot gone", snapshot.id);
      onSnapshotGone();
    } else if (code === "snapshot_outdated") {
      logger.debug("[maandrapport]", "snapshot outdated", snapshot.id);
      onOutdated?.();
    } else if ((error as Partial<ApiError> | null)?.status === 403) {
      logger.debug("[maandrapport]", "access lost");
      onDenied();
    }
  };
}

export function createMaandrapportSectionLoader(
  queryClient: QueryClient,
  snapshot: SnapshotRef,
  { fetchSection = fetchMaandrapportSection, ...options }: SectionLoaderOptions,
): SectionLoader {
  return cachedSectionLoader(snapshot, {
    fetch: (key) =>
      queryClient.fetchQuery({
        queryKey: maandrapportSectionKey(snapshot.id, key),
        queryFn: () => fetchSection(snapshot.id, key),
        staleTime: Infinity,
        retry: retryable,
      }),
    codeOf: apiErrorCode,
    onFailure: failureHandler(snapshot, options),
  });
}

/** The view of one range of the snapshot, once per range. */
export function createMaandrapportViewLoader(
  queryClient: QueryClient,
  snapshot: SnapshotRef,
  options: LoaderOptions,
): ViewLoader {
  const onFailure = failureHandler(snapshot, options);
  return async ({ from, to }) => {
    try {
      const view = await queryClient.fetchQuery({
        queryKey: maandrapportViewKey(snapshot.id, from, to),
        queryFn: () => fetchMaandrapportView(snapshot.id, from, to),
        staleTime: Infinity,
        retry: retryable,
      });
      return asView(view);
    } catch (error) {
      onFailure(error);
      throw error;
    }
  };
}

/** One detail of the snapshot, once per kind, key, range and moment. */
export function createMaandrapportDetailLoader(
  queryClient: QueryClient,
  snapshot: SnapshotRef,
  options: LoaderOptions,
): DetailLoader {
  const onFailure = failureHandler(snapshot, options);
  return async (request: DetailRequest) => {
    try {
      return await queryClient.fetchQuery({
        queryKey: maandrapportDetailKey(
          snapshot.id,
          request.kind,
          request.key,
          request.from,
          request.to,
          request.at ?? "this",
        ),
        queryFn: () => fetchMaandrapportDetail(snapshot.id, request),
        staleTime: Infinity,
        retry: retryable,
      });
    } catch (error) {
      onFailure(error);
      throw error;
    }
  };
}
